import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';

import AddTemperature from './AddTemperature';
import {
  isEnableLoadingSelector,
  temperatureListSelector,
} from './selector';
import { deleteTemperature, getTemperatureList, refreshTemperatureList } from './thunk';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { FacilityJson } from '@/types/domain/childApply/entities/facilityEntity';
import type { TemperatureJson } from '@/types/domain/temperature/entities/temperatureEntity';
import type { UserType } from '@/types/domain/user/redux';

import AdditionalLoading from '@/components/AdditionalLoading';
import Delete from '@/components/Delete';
import TableBase from '@/components/TableBase';
import TemperatureInfo from '@/domain/temperature/component/TemperatureInfo';
import { MeasureDateTime } from '@/domain/temperature/service/entity/valueObjects/measureDateTime';
import { Temperature as TemperatureVO } from '@/domain/temperature/service/entity/valueObjects/temperature';
import { toStringDateTimeDisplay } from '@/utils/date';

/** プロパティ */
export type Props = {
  /** ユーザー */
  user: UserType,
  /** 子ども */
  child: ChildJson,
  /** 施設リスト */
  facilityList: FacilityJson[],
};

const Temperature: React.FC<Props> = ({ user, child, facilityList }) => {
  const dispatch = useDispatch();
  const isEnableLoading = useSelector(isEnableLoadingSelector);
  const [
    isOpen,
    setIsOpen,
  ] = React.useState<boolean>(false);
  const temperatureList = useSelector(temperatureListSelector);
  const [
    deleteTemperatureJson,
    setDeleteTemperatureJson,
  ] = React.useState<TemperatureJson | undefined>(undefined);
  const rows: TableBaseRowType[] = [];
  for (let i = 0; i < temperatureList.length; i += 1) {
    if (temperatureList[i].data.childId === child.childId) {
      rows.push({
        id: i.toString(),
        measureDateTime:
          toStringDateTimeDisplay(new Date(temperatureList[i].data.measureDateTime), false),
        temperature: temperatureList[i].data.temperature.toString(),
        type: temperatureList[i].data.measurerType,
      });
    }
  }
  const columns: TableBaseColumnType[] = [
    { id: 'measureDateTime', label: MeasureDateTime.label, minWidth: 110 },
    { id: 'temperature', label: TemperatureVO.label, minWidth: 20 },
  ];
  const actions = [
    {
      icon: <DeleteIcon />,
      title: '削除',
      action: (r: TableBaseRowType) => {
        const item = temperatureList[parseInt(r.id, 10)];
        setDeleteTemperatureJson(item);
      },
      disabled: (r: TableBaseRowType) => r.type !== 'app',
    },
  ];
  return (
    <div>
      <div>
        <TableBase
          rows={rows}
          columns={columns}
          actions={actions}
          addAction={() => setIsOpen(true)}
          refreshAction={() => dispatch(refreshTemperatureList())}
          isPagination={false}
          isShowFilter={false}
          isShowFooter={false}
        />
        <AdditionalLoading
          isEnableLoading={isEnableLoading}
          onClick={() => dispatch(getTemperatureList())}
        />
        <AddTemperature
          isOpen={isOpen}
          close={() => setIsOpen(false)}
          child={child}
          user={user}
          facilityList={facilityList}
        />
        <Delete<TemperatureJson>
          comp={TemperatureInfo}
          data={deleteTemperatureJson}
          onDelete={(json) => {
            if (json) {
              dispatch(deleteTemperature({
                data: json,
                facilityIds: facilityList.map((v) => v.data.facilityId),
              }));
            }
            setDeleteTemperatureJson(undefined);
          }}
          title="体温測定"
          content="以下の記録を削除しますか？"
        />
      </div>
    </div>
  );
};

export default Temperature;
